import { useQuery } from "@tanstack/react-query";
import { getSocialDisclosedNames } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

export const disclosedNamesKeys = {
  all: ["SOCIAL_DISCLOSED_NAMES"] as const,
};

/**
 * The FULL set of people whose real name this viewer may see on a private profile — mutual
 * followers plus the people they exchanged message requests with (App ADR-0011) — keyed by
 * persona id (identityUserPublicID, which IS the Stream chat user id), valued by the name to
 * show. This is what upgrades the @handle that Stream carries for a private persona into their
 * real name (ADR-0007): the list came from the one endpoint that applied the disclosure rule
 * server-side, so the client only ever learns names it was entitled to. Stream itself never
 * learns them.
 *
 * One unpaged request (App ADR-0014): the set must be complete to resolve any member the chat
 * renders, and paging it cost a sorted round trip per 100 rows on every refresh. Only id, handle
 * and name travel, so thousands of rows stay small.
 *
 * Distinct from useMutualFollowerIdsQuery, which stays a follow fact (the Mutual tab).
 */
export const useDisclosedNamesMapQuery = () =>
  useQuery<ReadonlyMap<string, string>>({
    queryKey: disclosedNamesKeys.all,
    queryFn: async () => {
      const fetchAll = processResponseWithApiProblemDetails(() =>
        getSocialDisclosedNames(),
      );
      const names = await fetchAll();

      return new Map(
        names.map(({ identityUserPublicID, displayName }) => [
          identityUserPublicID,
          displayName,
        ]),
      );
    },
    staleTime: MS_TIME.minute,
  });
